import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { PasswordInput } from '@/components/sv/password-input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getDatabaseVersions } from '@/config/database-versions'
import { databases } from '@/config/databases'
import type { ClusterProvider } from '@/graphql.types'
import type { CreateDatabaseFormValues } from '@/routes/databases/create-database/form/schema'

type Props = {
  provider?: ClusterProvider
}

export const DbData = ({ provider }: Props) => {
  const form = useFormContext<CreateDatabaseFormValues>()
  const { resetField } = form
  const type = form.watch('type')
  const version = form.watch('version')
  const dbConfig = databases?.[type]

  const versions = useMemo(
    () => getDatabaseVersions({ type, provider }),
    [type, provider]
  )

  useEffect(() => {
    return () => {
      resetField('data')
    }
  }, [resetField])

  useEffect(() => {
    const isVersionSupported = versions.includes(form.getValues('version'))
    if (!isVersionSupported) {
      form.setValue('version', versions[0] ?? '', { shouldValidate: true })
    }
    form.trigger(['extensions.enableVector', 'extensions.enablePostgis'])
  }, [versions])

  useEffect(() => {
    form.trigger(['extensions.enableVector', 'extensions.enablePostgis'])
  }, [version])

  return (
    <>
      <FormField
        control={form.control}
        name="version"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{dbConfig?.name} version</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a database version" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {versions.map((version) => {
                  return (
                    <SelectItem key={version} value={version}>
                      {version}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {type === 'postgresql' ? (
        <>
          <FormField
            control={form.control}
            name="extensions.enableVector"
            render={({ field: { value, onChange, ...field } }) => (
              <FormItem className="flex items-start">
                <FormControl>
                  <Checkbox
                    checked={Boolean(value)}
                    onCheckedChange={onChange}
                    {...field}
                  />
                </FormControl>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <FormLabel className="cursor-pointer">
                      Enable pgvector extension
                    </FormLabel>
                    <FormDescription>
                      This extension provides vector similarity search
                      capabilities to your PostgreSQL database.
                    </FormDescription>
                    <FormMessage />
                  </div>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="extensions.enablePostgis"
            render={({ field: { value, onChange, ...field } }) => (
              <FormItem className="flex items-start">
                <FormControl>
                  <Checkbox
                    checked={Boolean(value)}
                    onCheckedChange={onChange}
                    {...field}
                  />
                </FormControl>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <FormLabel className="cursor-pointer">
                      Enable PostGIS extension
                    </FormLabel>
                    <FormDescription>
                      This extension adds support for geographic objects to the
                      PostgreSQL database.
                    </FormDescription>
                    <FormMessage />
                  </div>
                </div>
              </FormItem>
            )}
          />
        </>
      ) : null}
      <FormField
        control={form.control}
        name="data.dbName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Database name</FormLabel>
            <FormControl>
              <Input autoComplete="off" aria-label="Database name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {type !== 'redis' && type !== 'valkey' ? (
        <FormField
          control={form.control}
          name="data.dbUser"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Database user</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  aria-label="Database user"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      <FormField
        control={form.control}
        name="data.dbPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Database password</FormLabel>
            <FormControl>
              <PasswordInput autoComplete="off" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}
